/**
 * Crawler detection for the credit-gate bypass.
 *
 * The admin "crawler indexing access" flag opens prediction picks and analysis
 * so search engines and AI agents can index the real content. That bypass must
 * apply to bots only — a human visitor always meets the credit gate, otherwise
 * the whole economy is switched off whenever the flag is ON.
 *
 * This is deliberately *not* cloaking: the flag does not change canonical tags,
 * robots directives, or the prerendered HTML crawlers fetch. It only decides
 * whether an interactive session is treated as a bot session.
 */

/** User-agent fragments used by search, social and AI crawlers. */
const CRAWLER_UA = new RegExp(
  [
    // Search engines
    "googlebot",
    "google-inspectiontool",
    "storebot-google",
    "adsbot-google",
    "mediapartners-google",
    "bingbot",
    "bingpreview",
    "adidxbot",
    "slurp",
    "duckduckbot",
    "duckduckgo",
    "baiduspider",
    "yandex(bot|images|mobilebot)",
    "naver",
    "yeti",
    "seznambot",
    "sogou",
    "exabot",
    "petalbot",
    "applebot",
    "qwantify",
    "ecosia",
    "brave",
    // AI / answer engines
    "gptbot",
    "oai-searchbot",
    "chatgpt-user",
    "claudebot",
    "claude-web",
    "anthropic-ai",
    "perplexitybot",
    "perplexity-user",
    "google-extended",
    "ccbot",
    "cohere-ai",
    "meta-externalagent",
    "meta-externalfetcher",
    "facebookexternalhit",
    "bytespider",
    "amazonbot",
    "youbot",
    "diffbot",
    "mistralai-user",
    // Generic
    "crawler",
    "spider",
    "\\bbot\\b",
    "headlesschrome",
    "lighthouse",
    "chrome-lighthouse",
    "prerender",
  ].join("|"),
  "i",
);

export function isCrawlerUserAgent(userAgent: string | undefined | null): boolean {
  if (!userAgent) return false;
  return CRAWLER_UA.test(userAgent);
}

/**
 * True when the current runtime is a crawler, a headless auditor, or the
 * prerender pass — never for an ordinary browser session.
 */
export function isCrawlerClient(): boolean {
  if (typeof window === "undefined") return true; // build-time / SSR render
  const w = window as unknown as { __PRERENDER__?: boolean };
  if (w.__PRERENDER__ === true) return true;
  return isCrawlerUserAgent(navigator.userAgent);
}
